import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Lock, BookOpen } from "lucide-react";

const GATE_LABELS = { green: "GREEN", amber: "AMBER", red: "RED" };
const GATE_STYLES = {
  green: "bg-emerald-100 border-emerald-300 text-emerald-800",
  amber: "bg-amber-100 border-amber-300 text-amber-800",
  red: "bg-red-100 border-red-300 text-red-700",
};
const FT_STYLES = {
  pass: "bg-emerald-100 border-emerald-300 text-emerald-800",
  management_review: "bg-amber-100 border-amber-300 text-amber-800",
  fail: "bg-red-100 border-red-300 text-red-700",
};

function Chip({ label, style, testid }) {
  if (!label) return <span className="text-slate-400 text-xs">—</span>;
  return <span className={`inline-block px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${style}`} data-testid={testid}>{label}</span>;
}

export default function StaffTraining() {
  const { user } = useAuth();
  const [trainees, setTrainees] = useState(null);
  const isManagement = user?.role === "management_admin";

  useEffect(() => {
    api.get("/staff/training/trainees").then((r) => setTrainees(r.data)).catch(() => toast.error("Could not load trainees"));
  }, []);

  if (!trainees) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="staff-training-loading" />;

  return (
    <div className="space-y-6" data-testid="staff-training-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-lomond mb-1">Lomond Operator Training Camp</p>
          <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl sm:text-3xl" data-testid="staff-training-heading">Training Camp Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Day-by-day progress, attendance, quizzes, sign-offs, Week 1 gate and Day 10 provider gate.</p>
        </div>
        {isManagement && (
          <Link to="/staff/training/content">
            <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-lomond text-black font-bold text-sm hover:bg-[#E6B400]" data-testid="training-content-btn">
              <BookOpen className="h-4 w-4" /> Training Content Admin
            </span>
          </Link>
        )}
      </div>

      {!trainees.length && <p className="text-sm text-slate-500 bg-white border border-steel rounded-md p-6" data-testid="no-trainees">No trainees yet. Admit candidates from the assessment pipeline.</p>}

      <div className="grid lg:grid-cols-2 gap-4" data-testid="trainee-list">
        {trainees.map((t) => (
          <Link key={t.id} to={`/staff/training/${t.id}`} className="block" data-testid={`trainee-card-${t.id}`}>
            <div className="bg-white border border-steel rounded-md p-5 hover:border-lomond transition-colors h-full">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-heading font-bold uppercase text-charcoal">{t.name}</p>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">Day {t.current_day} of 10{t.external_status ? ` — ${t.external_status.replace(/_/g, " ")}` : ""}</p>
                </div>
                <div className="flex gap-1" data-testid={`day-dots-${t.id}`}>
                  {Array.from({ length: 10 }, (_, i) => {
                    const d = i + 1;
                    const done = d < t.current_day || (d === 10 && t.current_day === 10 && t.stats.modules_completed === 10);
                    const current = d === t.current_day && t.stats.modules_completed < 10;
                    return (
                      <span key={d} className={`h-5 w-5 rounded-sm flex items-center justify-center border ${
                        done ? "bg-emerald-500 border-emerald-500 text-white" : current ? "bg-lomond border-lomond text-black" : "bg-elevated border-steel text-slate-400"
                      }`} title={`Day ${d}`}>
                        {done ? <Check className="h-3 w-3" /> : current ? <span className="text-[9px] font-black">{d}</span> : <Lock className="h-2.5 w-2.5" />}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                <div className="bg-elevated rounded-sm p-2">
                  <p className="font-mono font-bold text-charcoal text-sm" data-testid={`theory-avg-${t.id}`}>{t.stats.theory_avg != null ? `${t.stats.theory_avg}%` : "—"}</p>
                  <p className="text-[8px] font-mono uppercase tracking-widest text-slate-500">Theory avg</p>
                </div>
                <div className="bg-elevated rounded-sm p-2">
                  <p className="font-mono font-bold text-charcoal text-sm">{t.stats.attendance.present ?? 0}/{t.stats.modules_completed || t.current_day}</p>
                  <p className="text-[8px] font-mono uppercase tracking-widest text-slate-500">Present</p>
                </div>
                <div className="bg-elevated rounded-sm p-2">
                  <p className="font-mono font-bold text-charcoal text-sm">{t.stats.practical_done}/10</p>
                  <p className="text-[8px] font-mono uppercase tracking-widest text-slate-500">Practical</p>
                </div>
                <div className="bg-elevated rounded-sm p-2">
                  <p className={`font-mono font-bold text-sm ${t.stats.safety_concerns > 0 ? "text-red-600" : "text-charcoal"}`}>{t.stats.safety_concerns}</p>
                  <p className="text-[8px] font-mono uppercase tracking-widest text-slate-500">Safety</p>
                </div>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap items-center text-[10px] font-mono uppercase tracking-widest">
                <span className="text-slate-400">W1 gate:</span>
                <Chip label={GATE_LABELS[t.week1_decision]} style={GATE_STYLES[t.week1_decision]} testid={`week1-${t.id}`} />
                <span className="text-slate-400 ml-2">Theory:</span>
                <Chip label={t.final_theory_status?.replace(/_/g, " ")} style={FT_STYLES[t.final_theory_status]} testid={`ft-${t.id}`} />
                <span className="text-slate-400 ml-2">Gate:</span>
                <Chip label={t.final_gate?.replace(/_/g, " ")} style={t.final_gate === "approved_external" ? GATE_STYLES.green : t.final_gate === "extend_lomond" ? GATE_STYLES.amber : t.final_gate ? GATE_STYLES.red : ""} testid={`gate-${t.id}`} />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
